import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CheckroomOutlinedIcon from "@mui/icons-material/CheckroomOutlined";
import ElectricBoltOutlinedIcon from "@mui/icons-material/ElectricBoltOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import DeskOutlinedIcon from "@mui/icons-material/DeskOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import BackHandOutlinedIcon from "@mui/icons-material/BackHandOutlined";
import PinDropOutlinedIcon from "@mui/icons-material/PinDropOutlined";
import Layout from "components/Layout";
import homepageHero1 from "assets/images/Homepage_Hero_2.png";
import homepageHero2 from "assets/images/Homepage_Hero_1.png";
import spaCosmeticImg from "assets/images/spa_cosmetic_img.png";
import fitnessSportImg from "assets/images/fitness_sport_img.png";
import unternehmerImg from "assets/images/unternehmer_img.png";

const HomePage = () => {
  const location = useLocation();

  useEffect(() => {
    const items = document.querySelectorAll(".fade-in");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-visible");
        });
      },
      { threshold: 0.16 }
    );
    items.forEach((it) => observer.observe(it));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!location.hash) return undefined;

    const hashId = decodeURIComponent(location.hash.slice(1));
    const targetId = hashId === "lizenz" ? "lizenzmodell" : hashId;
    const timeoutId = window.setTimeout(() => {
      const target = document.getElementById(targetId);
      if (!target) return;

      const navHeight = document.querySelector("nav")?.offsetHeight || 0;
      const targetTop = target.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: Math.max(targetTop - navHeight - 14, 0),
        behavior: "smooth",
      });
    }, 80);

    return () => window.clearTimeout(timeoutId);
  }, [location.hash]);

  return (
    <Layout>
      {/* ─── 1. Hero ─── */}
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-content">
            <h1>
              <span className="hero-mobile-hide">Die </span>D<span className="hero-hoffmann-smallcaps">r</span>. H<span className="hero-hoffmann-smallcaps">offmann</span> Massage
            </h1>
            <p className="hero-lead">
              Schnell. Smart. Skalierbar.
            </p>
            <div className="hero-actions">
              <Link to="/buchen" className="btn-primary">
                Jetzt Termin buchen
              </Link>
            </div>
          </div>

          <div className="hero-visual hero-photo-composition fade-in" aria-label="Dr. Hoffmann Massage Impressionen">
            <figure className="hero-photo hero-photo-main">
              <img src={homepageHero1} alt="Dr. Hoffmann Massage Anwendung in Hamburg" />
            </figure>
            <figure className="hero-photo hero-photo-secondary">
              <img src={homepageHero2} alt="Dr. Hoffmann Massage Ambiente" />
            </figure>
          </div>
        </div>
      </section>

      {/* ─── 2. Idea ─── */}
      <section className="section" id="konzept">
        <div className="fade-in">
          <span className="section-kicker">Das Konzept</span>
          <h2 className="section-title">
            Wellness, Effizienz und <em>moderne Vertriebsstrukturen</em> — in einem System.
          </h2>
        </div>
        <div className="two-col">
          <p className="section-lead fade-in">
            Das Dr. Hoffmann-Prinzip verbindet kurze, intensive Massage-Sessions
            mit einem skalierbaren Lizenz- und Hybrid-Vertriebsmodell. Mehr
            Buchungen, mehr Sichtbarkeit und ein höherer Stundenumsatz für
            Studios, Unternehmer und mobile Anbieter.
          </p>
          <ul className="two-col-list fade-in">
            <li>Mehr Buchungen</li>
            <li>Höherer Umsatz pro Stunde</li>
            <li>Hybrider Vertriebsweg</li>
            <li>Schnell buchbar</li>
            <li>Einfach skalierbar</li>
            <li>Sofort vermarktbar</li>
          </ul>
        </div>
      </section>

      {/* ─── 3. Why Dr. Hoffmann-Massage ─── */}
      <section className="section section-alt why-section">
        <div className="why-section-inner">
          <div className="why-heading fade-in">
            <span className="section-kicker">Warum Dr. Hoffmann?</span>
            <h2 className="section-title">
              Fünf Gründe, die das <em>Prinzip einzigartig</em> machen.
            </h2>
          </div>

          <div className="why-visual" aria-hidden="true">
            <div className="why-flow why-flow-a" />
            <div className="why-flow why-flow-b" />
            <div className="why-core">
              <span />
            </div>
          </div>

          <div className="why-code-grid">
            <article className="why-code-card why-code-card-1 fade-in">
              <AccessTimeOutlinedIcon className="why-code-icon" />
              <h3>Schnell</h3>
              <p>Nur ca. 10 Minuten pro Session — maximale Wirkung in minimaler Zeit.</p>
            </article>
            <article className="why-code-card why-code-card-2 fade-in">
              <AutoAwesomeOutlinedIcon className="why-code-icon" />
              <h3>Effektiv</h3>
              <p>4-in-1 Massagekonzept, das tief liegende Muskelpartien gezielt anspricht.</p>
            </article>
            <article className="why-code-card why-code-card-3 fade-in">
              <CheckroomOutlinedIcon className="why-code-icon" />
              <h3>Einfach</h3>
              <p>Behandlung in Alltagskleidung möglich — kein Umkleiden, kein Aufwand.</p>
            </article>
            <article className="why-code-card why-code-card-4 fade-in">
              <ElectricBoltOutlinedIcon className="why-code-icon" />
              <h3>Flüsterleise</h3>
              <p>EQ-150 Motor für diskreten Einsatz in Studios, Büros und bei Events.</p>
            </article>
            <article className="why-code-card why-code-card-5 fade-in">
              <div className="why-code-icon-group">
                <ApartmentOutlinedIcon />
                <DeskOutlinedIcon />
                <EventAvailableOutlinedIcon />
              </div>
              <h3>Flexibel</h3>
              <p>Überall einsetzbar — im Studio, Büro, bei Events oder Gesundheitstagen.</p>
            </article>
          </div>
        </div>
      </section>

      {/* ─── 4. License model ─── */}
      <section className="license-system" id="lizenzmodell">
        <div className="license-system-inner">
          <div className="license-system-copy fade-in">
            <span className="license-kicker">Das Lizenzmodell</span>
            <h2>
              Wir liefern Ihnen das <em>komplette System</em>.
            </h2>
            <p>
              Sie konzentrieren sich auf Ihre Kunden — wir stellen Ihnen die
              Infrastruktur, das Marketing und den Vertrieb zur Verfügung, damit
              Sie das Angebot schnell und professionell starten können.
            </p>
            <Link to="/buchen" className="license-system-cta license-system-cta-desktop">
              Jetzt Termin buchen
            </Link>
          </div>
          <div className="license-system-map fade-in">
            <h3>Wir übernehmen:</h3>
            <ul>
              <li>Onboarding</li>
              <li>Hybrid-Marketing & Kampagnen</li>
              <li>Buchung & Terminverwaltung</li>
              <li>CRM-System</li>
              <li>Gebietsentwicklung nach Postleitzahl</li>
              <li>Vertriebsunterstützung</li>
            </ul>
          </div>
          <Link to="/buchen" className="license-system-cta license-system-cta-mobile">
            Jetzt Termin buchen
          </Link>
        </div>
      </section>

      {/* ─── 5. Target groups ─── */}
      <section className="target-section">
        <div className="target-heading fade-in">
          <span className="section-kicker">Zielgruppen</span>
          <h2 className="section-title">
            Für wen ist das <em>Angebot gemacht</em>?
          </h2>
        </div>
        <div className="target-card-grid">
          {[
            {
              image: spaCosmeticImg,
              title: "Wellness & Kosmetik",
              body: "Optimale Ergänzung für bestehende Studios — neue Kunden, höhere Frequenz.",
            },
            {
              image: fitnessSportImg,
              title: "Fitness & Sport",
              body: "Regeneration für Mitglieder als Zusatz-Service mit klarer Marge.",
            },
            {
              image: unternehmerImg,
              title: "Unternehmer",
              body: "Ideal für Top-Locations, mobile Anbieter, Event- und Gesundheitstage.",
            },
          ].map((c) => (
            <article className="target-card fade-in" key={c.title}>
              <img src={c.image} alt="" loading="lazy" />
              <div className="target-card-copy">
                <h3>{c.title}</h3>
                <p>{c.body}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ─── 6. Pricing ─── */}
      <section className="section" id="tarife">
        <div className="fade-in">
          <span className="section-kicker">Unsere Tarife</span>
          <h2 className="section-title">
            Klare Preise — <em>direkt vermarktbar</em>.
          </h2>
        </div>
        <div className="pricing-grid">
          <div className="pricing-card fade-in is-featured">
            <h3>Wake-Up Massage im Studio</h3>
            <div className="pricing-price">
              5 € <small>p.P.</small>
            </div>
            <p>Ideal für Laufkundschaft und schnellen Durchlauf im Studio oder Center.</p>
            <div style={{ marginTop: "1.4rem" }}>
              <Link to="/buchen" className="btn-primary">Termin im Studio</Link>
            </div>
          </div>
          <div className="pricing-card fade-in">
            <h3>Wake-Up Massage vor Ort</h3>
            <div className="pricing-price">
              5 € <small>zzgl. Anfahrtskosten</small>
            </div>
            <p>Perfekt für Büros, Events, Gesundheitstage und mobile Anbieter mit Premium-Anspruch.</p>
            <div style={{ marginTop: "1.4rem" }}>
              <Link to="/buchen" className="btn-ghost">Mobile Buchung</Link>
            </div>
          </div>
        </div>
        <p
          className="section-lead fade-in"
          style={{ textAlign: "center", margin: "2.2rem auto 0", fontStyle: "italic" }}
        >
          Schnell buchbar. Einfach skalierbar. Sofort vermarktbar.
        </p>
      </section>

      {/* ─── 7. Market comparison ─── */}
      <section className="section section-alt market-section">
        <div className="fade-in">
          <span className="section-kicker">Marktvergleich</span>
          <h2 className="section-title">
            Verdienstmöglichkeiten im <em>direkten Vergleich</em>.
          </h2>
        </div>
        <div className="market-visual fade-in" aria-hidden="true">
          <div className="market-side market-side-classic">
            <div className="market-metric">
              <span>Umsatz / Std</span>
              <strong>ca. 70 €</strong>
              <small>Dauer<br />60m</small>
            </div>
            <div className="market-pyramid market-pyramid-classic">
              <span />
            </div>
            <p>Klassischer Massage-Markt</p>
          </div>
          <div className="market-growth">
            <svg viewBox="0 0 230 110" role="presentation" focusable="false">
              <defs>
                <linearGradient id="marketArrowGradient" x1="0" x2="1" y1="0" y2="0">
                  <stop offset="0%" stopColor="#6fd7ff" stopOpacity="0" />
                  <stop offset="58%" stopColor="#6fd7ff" />
                  <stop offset="100%" stopColor="#7eecff" />
                </linearGradient>
                <filter id="marketArrowGlow" x="-40%" y="-60%" width="180%" height="220%">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <path d="M16 76 C 74 92, 126 58, 174 18" />
              <path d="M156 12 L190 4 L184 39" />
            </svg>
            <strong>+63%</strong>
          </div>
          <div className="market-side market-side-hoffmann">
            <div className="market-pyramid market-pyramid-glow">
              <span />
            </div>
            <div className="market-metric">
              <span>Umsatz / Std</span>
              <strong>114 €</strong>
              <small>Dauer<br />10m</small>
            </div>
            <p>Dr. Hoffmann-Massage</p>
          </div>
        </div>
        <div className="compare-grid" aria-label="Vergleichsdaten">
          <div className="compare-card is-classic fade-in">
            <h3>Klassischer Massage-Markt</h3>
            <div className="compare-stat"><span>Dauer</span><strong>60 Min.</strong></div>
            <div className="compare-stat"><span>Umsatz / Sitzung</span><strong>ca. 70 €</strong></div>
            <div className="compare-stat"><span>Umsatz / Stunde</span><strong>ca. 70 €</strong></div>
          </div>
          <div className="compare-card is-highlight fade-in">
            <h3>Dr. Hoffmann-Massage</h3>
            <div className="compare-stat"><span>Dauer</span><strong>10 Min.</strong></div>
            <div className="compare-stat"><span>Behandlungen / Stunde</span><strong>6</strong></div>
            <div className="compare-stat"><span>Umsatz / Sitzung (UPE)</span><strong>19 €</strong></div>
            <div className="compare-stat"><span>Umsatz / Stunde</span><strong>114 €</strong></div>
          </div>
        </div>
        <p className="compare-callout fade-in">
          Das Dr. Hoffmann-Prinzip ermöglicht bei deutlich kürzerer Behandlungsdauer{" "}
          <strong>+63 % höheren Stundenumsatz</strong>{" "}
          als klassische Massageangebote.
        </p>
        <p
          className="revenue-note fade-in"
          style={{ textAlign: "center", marginTop: "1.4rem" }}
        >
          Rechenweg: 29 € Studio-Tarif − 10 € Dienstleistungspauschale = 19 € pro Sitzung · 6 × 19 € = 114 € / Stunde.
        </p>
      </section>

      {/* ─── 8. Revenue model ─── */}
      <section className="section revenue-section">
        <div className="fade-in">
          <span className="section-kicker">Umsatzmodell</span>
          <h2 className="section-title">
            Beispielrechnung für <em>Lizenznehmer</em>.
          </h2>
          <p className="section-lead">
            Basis: 6 Massagen pro Stunde nach UPE. Beispielrechnung vor Steuern und Fixkosten.
          </p>
        </div>
        <div className="revenue-grid">
          <div className="revenue-card fade-in">
            <span className="revenue-tag">Vor Ort</span>
            <h3>Massageservice im Studio</h3>
            <div className="revenue-stats">
              <div className="revenue-stat-row"><span>Umsatz pro Stunde</span><strong>114 €</strong></div>
              <div className="revenue-stat-row"><span>Umsatz pro Monat</span><strong>9.120 €</strong></div>
            </div>
            <p>Stationäre Präsenz im Center · idealer Kundenfluss & hohe Sichtbarkeit.</p>
          </div>
          <div className="revenue-card fade-in">
            <span className="revenue-tag">Mobil</span>
            <h3>Flexibler Außeneinsatz</h3>
            <div className="revenue-stats">
              <div className="revenue-stat-row"><span>Umsatz pro Stunde</span><strong>174 €</strong></div>
              <div className="revenue-stat-row"><span>Umsatz pro Monat</span><strong>13.920 €</strong></div>
            </div>
            <p>Höhere Flexibilität & Reichweite · attraktives Premium-Preissegment.</p>
          </div>
        </div>
        <p className="revenue-note fade-in" style={{ textAlign: "center" }}>
          Beispielrechnung vor Steuern und Fixkosten · keine Garantie auf bestimmte Einnahmen.
        </p>
      </section>

      {/* ─── 9. Starter package ─── */}
      <section className="section starter-section">
        <div className="starter fade-in">
          <div>
            <span className="starter-tag">Juni-Aktion 2026</span>
            <h3>Unser Starterpaket — alles, was Sie zum Start brauchen.</h3>
            <ul>
              <li>Perkussions-Massagegerät</li>
              <li>1× Ausbildung</li>
              <li>1× Zertifikat</li>
              <li>Zugang CRM & Buchungssystem</li>
              <li>Einbindung Hybrid-Vertrieb</li>
              <li>Lebenslange Dr. Hoffmann-Prinzip Lizenz</li>
            </ul>
          </div>
          <div className="starter-price">
            <div className="label">Einmalig</div>
            <div className="amount">900 €</div>
            <div className="sub">Komplett-Setup inkl. Lizenz</div>
            <Link to="/buchen" className="btn-primary">Erstgespräch sichern</Link>
          </div>
        </div>
      </section>

      {/* ─── 10. Expansion ─── */}
      <section className="section section-alt expansion-section">
        <div className="fade-in">
          <span className="section-kicker">Der Vertriebsweg</span>
          <h2 className="section-title">
            Das <em>Schneckenhaus-Prinzip</em>.
          </h2>
          <p className="section-lead">
            Wir starten im Herzen Hamburgs, Cremon 11, und dehnen uns PLZ-basiert
            über das gesamte Stadtgebiet und später bundesweit aus. Sichern Sie
            sich jetzt Ihren Gebietsschutz für Ihre Postleitzahl, bevor diese
            vergeben ist.
          </p>
        </div>
        <div className="expansion">
          <div className="fade-in">
            <h3 style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", marginBottom: "1rem" }}>
              So expandieren wir — PLZ für PLZ
            </h3>
            <ul className="two-col-list" style={{ gridTemplateColumns: "1fr" }}>
              <li>Schritt 1 — Hamburg Altstadt (20457)</li>
              <li>Schritt 2 — Erweiterung im 20xxx- und 22xxx-Gebiet</li>
              <li>Schritt 3 — Großraum Hamburg 21xxx + Umland</li>
              <li>Schritt 4 — Bundesweiter Ausbau über Master-Lizenzpartner</li>
            </ul>
            <div style={{ marginTop: "1.6rem" }}>
              <Link to="/buchen" className="btn-primary">PLZ-Gebiet anfragen</Link>
            </div>
          </div>
          <div className="expansion-visual fade-in" aria-hidden="true">
            <svg className="expansion-orbit" viewBox="0 0 620 620" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <radialGradient id="ring" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
                  <stop offset="18%" stopColor="#7df4ff" stopOpacity="0.95" />
                  <stop offset="46%" stopColor="#246dff" stopOpacity="0.44" />
                  <stop offset="100%" stopColor="#07133e" stopOpacity="0" />
                </radialGradient>
              </defs>
              <circle cx="310" cy="310" r="282" fill="none" stroke="#61dfff" strokeOpacity="0.08" strokeWidth="2" />
              <circle cx="310" cy="310" r="238" fill="none" stroke="#d9fbff" strokeOpacity="0.56" strokeWidth="4" />
              <circle cx="310" cy="310" r="214" fill="none" stroke="#80eaff" strokeOpacity="0.2" strokeWidth="1" />
              <circle cx="310" cy="310" r="164" fill="none" stroke="#69d8ff" strokeOpacity="0.28" strokeWidth="2" strokeDasharray="2 9" />
              <circle cx="310" cy="310" r="124" fill="none" stroke="#9cf4ff" strokeOpacity="0.38" strokeWidth="1.5" />
              <circle cx="310" cy="310" r="82" fill="none" stroke="#6de8ff" strokeOpacity="0.46" strokeWidth="2" />
              <circle cx="310" cy="310" r="48" fill="url(#ring)" />
              <circle cx="310" cy="310" r="24" fill="#eaffff" opacity="0.95" />
              <path
                className="expansion-orbit-path"
                d="M310 72 C 430 70 520 152 534 265 C 552 414 430 516 304 506 C 188 496 116 418 126 330 C 138 232 226 202 310 220 C 384 236 412 302 390 356 C 366 414 286 426 250 378"
                fill="none"
                stroke="#ffffff"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray="10 12"
              />
              <g className="expansion-sparks">
                <circle cx="310" cy="72" r="6" />
                <circle cx="180" cy="376" r="5" />
                <circle cx="425" cy="482" r="4" />
                <circle cx="505" cy="300" r="3.5" />
                <circle cx="235" cy="190" r="3" />
                <circle cx="390" cy="142" r="3" />
              </g>
              <g className="expansion-hud-lines">
                <path d="M92 258 h42 v82 h-42" />
                <path d="M476 174 h54 v108 h-54" />
                <path d="M440 526 h76 v44 h58" />
                <path d="M120 470 h72" />
                <path d="M520 410 h58" />
              </g>
              <text x="310" y="180" textAnchor="middle" fill="#ffffff" fontFamily="Athelas, serif" fontWeight="400" fontSize="22">
                Hamburg
              </text>
              <text x="310" y="200" textAnchor="middle" fill="#ffffff" fontFamily="Bicyclette, sans-serif" fontSize="16">
                Cremon 11 · 20457 
              </text>
            </svg>
            <div className="expansion-pin" />
          </div>
        </div>
      </section>

      {/* ─── 11. Juni-Aktion ─── */}
      <section className="campaign-section" id="aktion">
        <div className="campaign-heading fade-in">
          <span className="section-kicker">Exklusive Juni-Aktion</span>
          <h2 className="section-title">
            Jetzt Juni-Termin <em>sichern</em>.
          </h2>
        </div>
        <div className="campaign fade-in">
          <div className="campaign-grid">
            <div className="campaign-item">
              <MeetingRoomOutlinedIcon className="campaign-icon" />
              <h4>Gratis Besuch</h4>
              <p>Kostenlose Vorführung direkt<br></br>bei Ihnen im Studio.</p>
            </div>
            <div className="campaign-item">
              <div className="campaign-icon campaign-icon-combo">
                <BackHandOutlinedIcon />
                <span>6</span>
              </div>
              <h4>Max. 6 Test-Massagen</h4>
              <p>Live-Demonstration durch<br></br>Master Sura.</p>
            </div>
            <div className="campaign-item">
              <PinDropOutlinedIcon className="campaign-icon" />
              <h4>Location-Check</h4>
              <p>Einschätzung des Standorts und PLZ-Gebiets.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 12. Final CTA ─── */}
      <section className="final-cta" id="kontakt">
        <p>Starten Sie <b>jetzt</b> mit einem kostenlosen Erstgespräch. Entdecken Sie das Dr. Hoffmann-Prinzip - Schnell. Smart. Skalierbar.</p>
        <div className="final-cta-buttons">
          <Link to="/buchen" className="btn-primary">Dr. Hoffmann Massagen</Link>
          <a href="https://mediconsult.group" target="_blank" rel="noopener noreferrer" className="btn-outline">
            Dr. Hoffmann Consulting
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
